import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Upload,
  Receipt,
  Close,
  CheckCircle,
  Warning,
  CloudUpload,
} from '@mui/icons-material';
import { vendorAPI } from 'services/api';

const statusColors = {
  pending: '#FF9800',
  escrow_reserved: '#2196F3',
  invoice_verified: '#9C27B0',
  admin_approved: '#00BCD4',
  confirmed: '#4CAF50',
  processing: '#FF5722',
  shipped: '#3F51B5',
  delivered: '#4CAF50',
  cancelled: '#F44336',
  rejected: '#F44336',
};

const InvoiceUpload = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Upload dialog state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorAPI.getOrders();
      setOrders(response.data || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load orders.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleOpenUpload = (order) => {
    setSelectedOrder(order);
    setInvoiceNumber(order.invoice_number || '');
    setInvoiceFile(null);
    setUploadDialog(true);
  };

  const handleUploadInvoice = async () => {
    if (!invoiceNumber.trim()) {
      setError('Invoice number is required.');
      return;
    }
    if (!invoiceFile) {
      setError('Please select an invoice file.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      await vendorAPI.uploadInvoice(selectedOrder.id, invoiceNumber, invoiceFile);
      setSuccess(`Invoice ${invoiceNumber} uploaded successfully for order ${selectedOrder.order_number}.`);
      setUploadDialog(false);
      loadOrders(); // Refresh
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to upload invoice.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Filter orders that need invoices (pending, escrow_reserved, admin_approved)
  const needsInvoice = orders.filter(
    (o) => !o.invoice_number && ['pending', 'escrow_reserved', 'admin_approved'].includes(o.status)
  );
  const hasInvoice = orders.filter((o) => o.invoice_number);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          📄 Invoice Upload
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload invoices for your orders. Invoices must be verified before payment is processed.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Orders Needing Invoice */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Warning sx={{ color: '#FF9800', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Orders Requiring Invoice ({needsInvoice.length})
            </Typography>
          </Box>

          {needsInvoice.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#FFF3E0' }}>
                    <TableCell><strong>Order #</strong></TableCell>
                    <TableCell><strong>Buyer</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {needsInvoice.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.buyer_name}</TableCell>
                      <TableCell>
                        {order.order_date
                          ? new Date(order.order_date).toLocaleDateString('en-IN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        ₹{(order.total_amount || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status?.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: `${statusColors[order.status] || '#757575'}20`,
                            color: statusColors[order.status] || '#757575',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Upload />}
                          onClick={() => handleOpenUpload(order)}
                          sx={{ textTransform: 'none' }}
                        >
                          Upload Invoice
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
              <Typography color="text.secondary">
                All orders have invoices uploaded!
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Orders With Invoice */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Receipt sx={{ color: '#4CAF50', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Invoiced Orders ({hasInvoice.length})
            </Typography>
          </Box>

          {hasInvoice.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                    <TableCell><strong>Order #</strong></TableCell>
                    <TableCell><strong>Invoice #</strong></TableCell>
                    <TableCell><strong>Buyer</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Verified</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasInvoice.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.invoice_number}</TableCell>
                      <TableCell>{order.buyer_name}</TableCell>
                      <TableCell align="right">
                        ₹{(order.total_amount || 0).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.invoice_verified ? 'Verified' : 'Pending'}
                          size="small"
                          color={order.invoice_verified ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.status?.replace(/_/g, ' ').toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: `${statusColors[order.status] || '#757575'}20`,
                            color: statusColors[order.status] || '#757575',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Receipt sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography color="text.secondary">
                No invoiced orders yet.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Upload Invoice
            </Typography>
            <IconButton onClick={() => setUploadDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Order Number</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedOrder.order_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ₹{(selectedOrder.total_amount || 0).toLocaleString('en-IN')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Buyer</Typography>
                  <Typography variant="body1">{selectedOrder.buyer_name}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <TextField
            label="Invoice Number"
            fullWidth
            required
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="e.g., INV-2026-001"
          />

          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: '#2196F3', bgcolor: '#E3F2FD' },
              transition: 'all 0.3s',
            }}
            onClick={() => document.getElementById('invoice-file-input').click()}
          >
            <input
              id="invoice-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              hidden
              onChange={(e) => setInvoiceFile(e.target.files[0])}
            />
            <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            {invoiceFile ? (
              <Typography variant="body1" fontWeight="bold" color="primary">
                {invoiceFile.name}
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Click to select invoice file (PDF, JPG, PNG)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUploadDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadInvoice}
            disabled={uploading || !invoiceNumber.trim() || !invoiceFile}
            startIcon={uploading ? <CircularProgress size={16} /> : <Upload />}
          >
            {uploading ? 'Uploading...' : 'Upload Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceUpload;
